import { useState, useRef, useEffect, useCallback } from "react";
import { bylineText } from "@/features/auth/impersonation/authorByline";
import {
  MessageSquare,
  Search,
  MessageCircleOff,
  ArrowLeft,
  Plus,
  Paperclip,
  Mic,
  Send,
  Loader2,
  X,
  Users,
  Trash2,
  ChevronDown,
  Check,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import { useChatThreads, useUnreadChatCount } from "../api/useChatThreads";
import { useClearChatThreads } from "../api/useClearChatThreads";
import type { ChatThread } from "../api/useChatThreads";
import {
  useInternalChat,
  type ChatMessage,
} from "@/features/leads/api/useInternalChat";
import { useInternalChatMutations } from "@/features/leads/api/useInternalChatMutations";
import { useChatLeadSearch } from "../api/useChatLeadSearch";
import { ChatVoiceNote } from "@/features/leads/components/view-lead/components/Chat/components/ChatVoiceNote";
import { useMentionableUsers } from "../api/useMentionableUsers";
import { subscribeOpenChat } from "../utils/chatOpenBus";
import { toast } from "sonner";
import { afterPaint, flashMessage } from "../utils/jumpToMessage";
import {
  applyMention,
  filterMentionUsers,
  findMentionToken,
  initialsOf,
  mentionsPresentIn,
  type MentionRef,
  type MentionToken,
} from "../utils/mentionInput";
import { CHAT_BODY_CLASS, renderChatBody } from "../utils/renderChatBody";
import { useAutoGrowTextarea } from "../utils/useAutoGrowTextarea";
import useInfiniteScrollTrigger from "@/hooks/useInfiniteScrollTrigger";
import { formatDistanceToNow } from "date-fns";

type View = "threads" | "chat" | "new";

export default function ChatDropdown() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("threads");
  const [search, setSearch] = useState("");
  const [activeThread, setActiveThread] = useState<ChatThread | null>(null);
  const [newChatSearch, setNewChatSearch] = useState("");

  const { currentUser } = useAppSelector((state) => state.auth);
  const userId = currentUser?.user?.id;
  const unreadCount = useUnreadChatCount();
  const {
    threads,
    isLoading: threadsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useChatThreads(open);
  const clearChats = useClearChatThreads();
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setView("threads");
      setActiveThread(null);
      setSearch("");
      setNewChatSearch("");
    }
  }, [open]);

  const handleConfirmClear = async () => {
    await clearChats.mutateAsync();
    setConfirmClearOpen(false);
  };

  const handleOpenThread = (thread: ChatThread) => {
    setActiveThread(thread);
    setView("chat");
  };

  // Programmatic open — a new-message toast was clicked (chatOpenBus). Open
  // the popover and remember which lead's thread to land on; the resolution
  // effect below waits for the thread list (opening triggers its fetch).
  const [pendingOpenLeadId, setPendingOpenLeadId] = useState<number | null>(
    null
  );
  const [pendingOpenLeadName, setPendingOpenLeadName] = useState<string>("");
  // Message (and its deal channel) to land on once the chat view is up —
  // set by a mention/reply click-through, cleared after the jump.
  const [focusMessage, setFocusMessage] = useState<{
    id: string;
    childId: number | null;
  } | null>(null);
  useEffect(
    () =>
      subscribeOpenChat(({ leadId, leadName, messageId, childId }) => {
        setOpen(true);
        setView("threads");
        setActiveThread(null);
        setPendingOpenLeadId(leadId);
        setPendingOpenLeadName(leadName ?? "");
        setFocusMessage(
          messageId ? { id: messageId, childId: childId ?? null } : null,
        );
      }),
    []
  );
  useEffect(() => {
    if (pendingOpenLeadId == null || threadsLoading) return;
    const match = threads.find((t) => t.leadId === pendingOpenLeadId);
    if (match) {
      setActiveThread(match);
    } else {
      // Brand-new conversation the (possibly stale) list doesn't carry yet —
      // open a minimal thread the same way "start new chat" does; the
      // message fetch inside the chat view keys on leadId alone.
      setActiveThread({
        leadId: pendingOpenLeadId,
        leadName: pendingOpenLeadName || `Lead #${pendingOpenLeadId}`,
        phone: "",
        children: [],
        leadUsers: [],
        lastMessage: "",
        lastMessageAt: "",
        senderName: "",
        unreadCount: 0,
      });
    }
    setView("chat");
    setPendingOpenLeadId(null);
    setPendingOpenLeadName("");
  }, [pendingOpenLeadId, pendingOpenLeadName, threads, threadsLoading]);

  const handleStartNewChat = (lead: {
    id: number;
    name: string;
    phone: string;
  }) => {
    setActiveThread({
      leadId: lead.id,
      leadName: lead.name,
      phone: lead.phone,
      children: [],
      leadUsers: [],
      lastMessage: "",
      lastMessageAt: "",
      senderName: "",
      unreadCount: 0,
    });
    setView("chat");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl w-[1.5rem] h-[1.5rem] relative"
        >
          <MessageSquare className="size-5 text-[var(--colorblack-10)]" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 size-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={16}
        className="w-[460px] p-0 rounded-2xl shadow-2xl border border-[#e6e8e7] overflow-hidden z-[200] flex flex-col"
        style={{
          // Keep a *definite* height (the inner flex-1 list needs one to
          // scroll) but never taller than the space actually available below
          // the trigger — otherwise the tail of the list renders below the
          // viewport fold and, since the popover is position:fixed, becomes
          // unreachable. `--radix-popover-content-available-height` already
          // accounts for sideOffset + collisionPadding.
          height: "min(580px, var(--radix-popover-content-available-height))",
        }}
      >
        {view === "threads" && (
          <ThreadListView
            threads={threads}
            isLoading={threadsLoading}
            search={search}
            onSearchChange={setSearch}
            unreadCount={unreadCount}
            onSelectThread={handleOpenThread}
            onNewChat={() => setView("new")}
            onClearChats={() => setConfirmClearOpen(true)}
            canClear={threads.length > 0}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        )}
        {view === "chat" && activeThread && (
          <InlineChatView
            key={`${activeThread.leadId}:${focusMessage?.id ?? ""}`}
            thread={activeThread}
            userId={userId}
            onBack={() => setView("threads")}
            onClose={() => setOpen(false)}
            focusMessage={focusMessage}
            onFocusedMessage={() => setFocusMessage(null)}
          />
        )}
        {view === "new" && (
          <NewChatView
            search={newChatSearch}
            onSearchChange={setNewChatSearch}
            onSelectLead={handleStartNewChat}
            onBack={() => setView("threads")}
          />
        )}
      </PopoverContent>
      <ConfirmDialog
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirm={handleConfirmClear}
        title="Clear all chats?"
        tone="warning"
        warningTitle="This only clears your view"
        warningDescription="Other participants will still see these conversations. Threads will re-appear here as soon as a new message arrives."
        confirmLabel="Clear chats"
        loadingLabel="Clearing..."
        isLoading={clearChats.isPending}
      />
    </Popover>
  );
}

/* ─── Thread List ─────────────────────────────────────── */

function ThreadListView({
  threads,
  isLoading,
  search,
  onSearchChange,
  unreadCount,
  onSelectThread,
  onNewChat,
  onClearChats,
  canClear,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
}: {
  threads: ChatThread[];
  isLoading: boolean;
  search: string;
  onSearchChange: (s: string) => void;
  unreadCount: number;
  onSelectThread: (t: ChatThread) => void;
  onNewChat: () => void;
  onClearChats: () => void;
  canClear: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}) {
  const isSearching = search.trim().length > 0;
  const filtered = isSearching
    ? threads.filter(
        (t) =>
          t.leadName.toLowerCase().includes(search.toLowerCase()) ||
          t.phone.includes(search)
      )
    : threads;

  // Auto-load the next page when the sentinel scrolls into view. Disabled
  // while searching, since search filters only the already-loaded threads
  // (a short filtered list would otherwise keep the sentinel in view and
  // greedily pull every remaining page).
  const { ref: loadMoreRef } = useInfiniteScrollTrigger(
    fetchNextPage,
    hasNextPage && !isFetchingNextPage && !isSearching
  );

  const fmtTime = (d: string) => {
    try {
      return formatDistanceToNow(new Date(d), { addSuffix: true });
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-[#e6e8e7] bg-white">
        <div className="flex items-center gap-2">
          <p className="text-[15px] font-semibold text-[#1e302a]">Messages</p>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-[#218760] text-white text-[9px] font-bold">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canClear && (
            <button
              onClick={onClearChats}
              title="Clear all chats from your view"
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium text-[#697571] hover:text-[#da2a46] hover:bg-[#fdecef] transition-colors"
            >
              <Trash2 className="size-3.5" />
              <span>Clear</span>
            </button>
          )}
          <button
            onClick={onNewChat}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-[#218760] bg-[#eaf5f1] hover:bg-[#d6ebe3] transition-colors"
          >
            <Plus className="size-3" /> New Chat
          </button>
        </div>
      </div>
      <div className="shrink-0 px-3 py-2 border-b border-[#e6e8e7] bg-white">
        <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#f7f9f8] rounded-lg border border-[#e6e8e7]">
          <Search className="size-3.5 text-[#9ba3a0] shrink-0" />
          <input
            type="text"
            placeholder="Search chats..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="flex-1 bg-transparent border-none outline-none text-[12px] text-[#374741] placeholder:text-[#9ba3a0]"
          />
        </div>
      </div>
      <div
        className="flex-1 min-h-0 overflow-y-auto bg-white"
        style={{ scrollbarWidth: "thin" }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <span className="text-[11px] text-[#9ba3a0] animate-pulse">
              Loading...
            </span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <MessageCircleOff className="size-8 text-[#cdd1d0]" />
            <p className="text-[12px] text-[#697571]">
              {search ? "No matches" : "No chats yet"}
            </p>
            <button
              onClick={onNewChat}
              className="text-[11px] font-medium text-[#218760] hover:underline"
            >
              Start a new conversation
            </button>
          </div>
        ) : (
          <>
            {filtered.map((t) => (
            <button
              key={t.leadId}
              onClick={() => onSelectThread(t)}
              className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#f7f9f8] transition-colors border-b border-[#f0f2f1] ${t.unreadCount > 0 ? "bg-[#eaf5f1]/40" : ""}`}
            >
              <div className="size-9 rounded-full bg-[#eaf5f1] flex items-center justify-center shrink-0">
                <span className="text-[11px] font-semibold text-[#218760]">
                  {t.leadName
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p
                    className={`text-[13px] truncate ${t.unreadCount > 0 ? "font-bold text-[#1e302a]" : "font-medium text-[#374741]"}`}
                  >
                    {t.leadName}
                  </p>
                  <span className="text-[9px] text-[#9ba3a0] shrink-0">
                    {fmtTime(t.lastMessageAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <p className="text-[11px] text-[#697571] truncate">
                    {t.senderName && (
                      <span className="font-medium">{t.senderName}: </span>
                    )}
                    {t.lastMessage}
                  </p>
                  <div className="flex items-center gap-1 shrink-0 ml-1">
                    {t.unreadCount > 0 && (
                      <span className="size-[18px] rounded-full bg-[#218760] text-white text-[9px] font-bold flex items-center justify-center shrink-0">
                        {t.unreadCount > 9 ? "9+" : t.unreadCount}
                      </span>
                    )}
                    {(t.stageName || t.statusName || t.subStatusName) && (
                      <span
                        className="inline-flex items-center gap-0.5 max-w-[120px] px-1.5 py-[1px] rounded text-[9px] font-semibold bg-[#eaf5f1] text-[#15803d]"
                        title={[t.stageName, t.statusName, t.subStatusName].filter(Boolean).join(" · ")}
                      >
                        <span className="w-1 h-1 rounded-full bg-[#15803d] shrink-0" />
                        <span className="truncate">
                          {[t.stageName, t.statusName, t.subStatusName].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
            ))}
            {!isSearching && hasNextPage && (
              <div
                ref={loadMoreRef}
                className="flex items-center justify-center py-3"
              >
                {isFetchingNextPage && (
                  <Loader2 className="size-4 text-[#9ba3a0] animate-spin" />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Inline Chat ─────────────────────────────────────── */

function InlineChatView({
  thread,
  userId,
  onBack,
  onClose,
  focusMessage,
  onFocusedMessage,
}: {
  thread: ChatThread;
  userId: number | undefined;
  onBack: () => void;
  onClose: () => void;
  /** Message to scroll to + flash once loaded (mention / reply click-through). */
  focusMessage?: { id: string; childId: number | null } | null;
  onFocusedMessage?: () => void;
}) {
  const dispatch = useAppDispatch();

  const openLeadDrawer = () => {
    dispatch(setCurrentLeadId(String(thread.leadId)));
    onClose();
  };
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mentionToken, setMentionToken] = useState<MentionToken | null>(null);
  const [activeMention, setActiveMention] = useState(0);
  const [mentions, setMentions] = useState<MentionRef[]>([]);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ChatMessage | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<number | null>(
    focusMessage?.childId ?? null,
  );
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Who can be @mentioned: everyone who can see the lead / the open deal
  // (server's mentionable list; members on an older server).
  const { users: members } = useMentionableUsers(thread.leadId, selectedChildId);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  useAutoGrowTextarea(inputRef, text, 5);

  const { messages, isLoading, hasMore, fetchOlder, ensureMessageLoaded } =
    useInternalChat(thread.leadId, selectedChildId);

  // JUMP TO A MESSAGE (reply-preview click, mention click-through): page
  // older messages in until the target exists, then scroll to it and flash
  // it. The "stick to bottom" effect below must stay out of the way while
  // pages are being prepended, hence the suppress ref.
  const suppressAutoScrollRef = useRef(false);
  const jumpToMessage = useCallback(
    async (messageId: string) => {
      suppressAutoScrollRef.current = true;
      try {
        const loaded = await ensureMessageLoaded(messageId);
        await afterPaint();
        if (!loaded || !flashMessage(scrollRef.current, messageId)) {
          toast.info("That message is no longer available");
        }
      } finally {
        suppressAutoScrollRef.current = false;
      }
    },
    [ensureMessageLoaded],
  );
  const focusedOnceRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusMessage?.id || isLoading) return;
    if (focusedOnceRef.current === focusMessage.id) return;
    focusedOnceRef.current = focusMessage.id;
    void jumpToMessage(focusMessage.id).finally(() => onFocusedMessage?.());
  }, [focusMessage?.id, isLoading, jumpToMessage, onFocusedMessage]);
  const { sendMessage, uploadFile, markAsRead, deleteMessage, editMessage } =
    useInternalChatMutations();

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMessage.mutate(
      { messageId: pendingDelete.id },
      { onSettled: () => setPendingDelete(null) }
    );
  };

  // Edit mode: load the message into the box; edit and reply are exclusive.
  const startEdit = (msg: ChatMessage) => {
    setReplyTo(null);
    setEditing(msg);
    setText(msg.body ?? "");
    setMentions((msg.mentions ?? []) as MentionRef[]);
    setMentionToken(null);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
  };
  const cancelEdit = () => {
    setEditing(null);
    setText("");
    setMentions([]);
    setMentionToken(null);
  };
  const startReply = (msg: ChatMessage) => {
    if (editing) cancelEdit();
    setReplyTo(msg);
    inputRef.current?.focus();
  };

  // Restore the caret after a programmatic text change (mention insert).
  useEffect(() => {
    if (pendingCaretRef.current == null) return;
    const el = inputRef.current;
    const pos = pendingCaretRef.current;
    pendingCaretRef.current = null;
    if (el) {
      el.focus();
      el.setSelectionRange(pos, pos);
    }
  }, [text]);

  // Switching channel abandons an in-progress edit/reply.
  useEffect(() => {
    setEditing(null);
    setReplyTo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChildId]);

  const selectedChild =
    selectedChildId != null
      ? thread.children?.find((c) => c.id === selectedChildId)
      : null;
  const selectedLabel = selectedChild ? selectedChild.name : "All Chats";
  const hasApplications = (thread.children?.length ?? 0) > 0;

  useEffect(() => {
    if (suppressAutoScrollRef.current) return;
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, selectedChildId]);

  useEffect(() => {
    if (!userId || messages.length === 0) return;
    const unread = messages
      .filter(
        (m) =>
          m.senderId !== userId && !m.readBy.some((r) => r.userId === userId)
      )
      .map((m) => m.id);
    if (unread.length > 0)
      markAsRead.mutate({
        leadId: thread.leadId,
        childId: selectedChildId,
        messageIds: unread,
      });
  }, [messages, userId, thread.leadId, selectedChildId]);

  // @mention: the `@…` token under the CARET (may contain spaces)
  const syncMentionToken = (val: string, caret: number) => {
    setMentionToken(findMentionToken(val, caret));
    setActiveMention(0);
  };
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);
    syncMentionToken(val, e.target.selectionStart ?? val.length);
  };
  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    syncMentionToken(el.value, el.selectionStart ?? el.value.length);
  };

  const insertMention = (user: { id: number; name: string }) => {
    const el = inputRef.current;
    const caret = el?.selectionStart ?? text.length;
    const token = mentionToken ?? findMentionToken(text, caret);
    if (!token) return;
    const next = applyMention(text, caret, token, user.name);
    pendingCaretRef.current = next.caret;
    setText(next.text);
    setMentions((prev) =>
      prev.some((m) => m.userId === user.id)
        ? prev
        : [...prev, { userId: user.id, name: user.name }]
    );
    setMentionToken(null);
  };

  // Available users for @mention — use live members
  const mentionUsers = members.length > 0 ? members : thread.leadUsers || [];
  const filteredMentionUsers = mentionToken
    ? filterMentionUsers(mentionUsers, mentionToken.query)
    : [];
  const mentionOpen = mentionToken !== null && filteredMentionUsers.length > 0;

  const handleSend = () => {
    const body = text.replace(/\r\n?/g, "\n").trim();
    if (!body || sendMessage.isPending || editMessage.isPending) return;
    const finalMentions = mentionsPresentIn(body, mentions);
    if (editing) {
      if (body !== (editing.body ?? "").trim()) {
        editMessage.mutate({
          messageId: editing.id,
          body,
          mentions: finalMentions,
        });
      }
      cancelEdit();
      return;
    }
    sendMessage.mutate({
      leadId: thread.leadId,
      childId: selectedChildId,
      body,
      mentions: finalMentions.length > 0 ? finalMentions : undefined,
      replyToId: replyTo?.id,
    });
    setText("");
    setMentions([]);
    setReplyTo(null);
    setMentionToken(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveMention((i) => (i + 1) % filteredMentionUsers.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveMention(
          (i) =>
            (i - 1 + filteredMentionUsers.length) % filteredMentionUsers.length
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(
          filteredMentionUsers[activeMention] ?? filteredMentionUsers[0]
        );
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionToken(null);
        return;
      }
    }
    if (e.key === "Escape") {
      if (editing) {
        e.preventDefault();
        cancelEdit();
      } else if (replyTo) {
        e.preventDefault();
        setReplyTo(null);
      }
      return;
    }
    // Enter = send · Shift+Enter = new line (textarea default)
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFile = async (file: File) => {
    const media = await uploadFile.mutateAsync({
      leadId: thread.leadId,
      childId: selectedChildId,
      file,
    });
    const mime = (media.mediaMime || "").split("/")[0];
    sendMessage.mutate({
      leadId: thread.leadId,
      childId: selectedChildId,
      type:
        mime === "image" ? "image" : mime === "audio" ? "audio" : "document",
      mediaUrl: media.mediaUrl,
      mediaMime: media.mediaMime,
      mediaName: media.mediaName,
      mediaSize: media.mediaSize,
      replyToId: replyTo?.id,
    });
    setReplyTo(null);
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (timerRef.current) clearInterval(timerRef.current);
        setIsRecording(false);
        setRecordingTime(0);
        if (chunksRef.current.length === 0) return;
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const media = await uploadFile.mutateAsync({
          leadId: thread.leadId,
          childId: selectedChildId,
          file: blob as any,
        });
        sendMessage.mutate({
          leadId: thread.leadId,
          childId: selectedChildId,
          type: "audio",
          body: "Voice note",
          mediaUrl: media.mediaUrl,
          mediaMime: media.mediaMime,
          mediaName: "Voice note",
        });
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(
        () => setRecordingTime((t) => t + 1),
        1000
      );
    } catch {
      /* mic denied */
    }
  }, [thread.leadId, selectedChildId, uploadFile, sendMessage]);

  const stopRecording = () => mediaRecorderRef.current?.stop();
  const cancelRecording = () => {
    chunksRef.current = [];
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current?.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
    setRecordingTime(0);
  };

  const fmtT = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const isBusy =
    sendMessage.isPending || uploadFile.isPending || editMessage.isPending;
  // Use live members from API, fall back to thread cache
  const allUsers = members.length > 0 ? members : thread.leadUsers || [];
  const memberNames = allUsers.map((u) => u.name).slice(0, 4);
  const memberSuffix = allUsers.length > 4 ? ` +${allUsers.length - 4}` : "";

  return (
    <div className="flex flex-col h-full">
      {/* Header — team chat about lead, not chat with lead */}
      <div className="shrink-0 px-3 py-2.5 border-b border-[#e6e8e7] bg-[#218760]">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="size-4 text-white" />
          </button>
          {/* Stacked user avatars */}
          <div className="flex -space-x-2 shrink-0">
            {allUsers.slice(0, 3).map((u, i) => (
              <div
                key={u.id}
                className="size-7 rounded-full bg-white/20 border-2 border-[#218760] flex items-center justify-center"
                style={{ zIndex: 3 - i }}
              >
                <span className="text-[8px] font-semibold text-white">
                  {u.name
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
              </div>
            ))}
            {allUsers.length === 0 && (
              <div className="size-7 rounded-full bg-white/20 flex items-center justify-center">
                <Users className="size-3.5 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {/* Chat scope selector: All Chats (lead) or one application/child */}
            {hasApplications ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1 cursor-pointer hover:opacity-90 transition-opacity max-w-full"
                    >
                      <p className="text-[12px] font-semibold text-white truncate max-w-[230px]">
                        {selectedLabel}
                      </p>
                      <ChevronDown className="size-3 text-white/70 shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="z-[300] w-[240px] max-h-[280px] overflow-y-auto"
                  >
                    <DropdownMenuLabel className="text-[10px] font-normal text-muted-foreground">
                      Chat scope
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onSelect={() => setSelectedChildId(null)}
                      className="text-[12px] flex items-center justify-between gap-2"
                    >
                      <span>All Chats</span>
                      {selectedChildId == null && (
                        <Check className="size-3.5 text-[#218760]" />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] font-normal text-muted-foreground">
                      Applications
                    </DropdownMenuLabel>
                    {thread.children!.map((c) => (
                      <DropdownMenuItem
                        key={c.id}
                        onSelect={() => setSelectedChildId(c.id)}
                        className="text-[12px] flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{c.name}</span>
                        {selectedChildId === c.id && (
                          <Check className="size-3.5 text-[#218760] shrink-0" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <button
                  type="button"
                  onClick={openLeadDrawer}
                  title="Open lead"
                  className="block text-left text-[10px] text-white/60 truncate max-w-full hover:text-white hover:underline transition-colors"
                >
                  Re: {thread.leadName || "Team Chat"}
                  {thread.phone ? ` · ${thread.phone}` : ""}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={openLeadDrawer}
                title="Open lead"
                className="flex flex-col items-start min-w-0 cursor-pointer text-left max-w-full group"
              >
                <p className="text-[12px] font-semibold text-white truncate max-w-full group-hover:underline">
                  {thread.leadName || "Team Chat"}
                  {thread.phone ? ` · ${thread.phone}` : ""}
                </p>
                <p className="text-[10px] text-white/60 truncate max-w-full">
                  {memberNames.length > 0
                    ? `With: ${memberNames.join(", ")}${memberSuffix}`
                    : "No members yet"}
                </p>
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/10">
            <Users className="size-3 text-white/70" />
            <span className="text-[10px] text-white/70 font-medium">
              {allUsers.length}
            </span>
          </div>
        </div>
      </div>

      {/*
        Messages scroll container.
        `mt-auto` on the inner wrapper keeps short message lists pinned to the
        bottom without putting `justify-content: flex-end` on the overflow
        container — that combo makes overflowing content render above
        scrollTop=0 where the scrollbar can't reach it (the "chat not
        scrollable" bug that was already fixed in the main Chat.tsx).
      */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#f0f2f0] px-3 py-2"
        style={{ scrollbarWidth: "thin" }}
      >
        <div className="mt-auto">
          {hasMore && (
            <div className="flex justify-center py-1.5">
              <button
                onClick={fetchOlder}
                className="text-[10px] text-[#697571] px-2 py-0.5 rounded-full bg-white border border-[#e6e8e7]"
              >
                Load older
              </button>
            </div>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-[11px] text-[#9ba3a0] animate-pulse">
                Loading...
              </span>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-[11px] text-[#9ba3a0]">
                Send a message to start
              </p>
            </div>
          ) : (
            messages.map((msg) => (
              <MiniChatBubble
                key={msg.id}
                msg={msg}
                isMine={msg.senderId === userId && !msg.isSystem}
                onReply={() => startReply(msg)}
                onDelete={() => setPendingDelete(msg)}
                onEdit={
                  msg.type === "text" && !msg.mediaUrl
                    ? () => startEdit(msg)
                    : undefined
                }
                onJumpTo={jumpToMessage}
              />
            ))
          )}
        </div>
      </div>

      {/* Edit banner */}
      {editing && (
        <div className="shrink-0 px-3 py-1.5 bg-[#f7f9f8] border-t border-[#e6e8e7] flex items-center gap-2">
          <Pencil size={12} className="text-[#218760] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-[#218760]">
              Editing message
            </p>
            <p className="text-[10px] text-[#697571] truncate">
              {editing.body}
            </p>
          </div>
          <button onClick={cancelEdit} className="p-0.5" title="Cancel edit (Esc)">
            <X size={12} className="text-[#9ba3a0]" />
          </button>
        </div>
      )}

      {/* Reply preview */}
      {!editing && replyTo && (
        <div className="shrink-0 px-3 py-1.5 bg-[#f7f9f8] border-t border-[#e6e8e7] flex items-center gap-2">
          <div className="w-0.5 h-8 bg-[#218760] rounded-full shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-[#218760]">
              {replyTo.sender?.name}
            </p>
            <p className="text-[10px] text-[#697571] truncate">
              {replyTo.body || replyTo.type}
            </p>
          </div>
          <button onClick={() => setReplyTo(null)} className="p-0.5">
            <X size={12} className="text-[#9ba3a0]" />
          </button>
        </div>
      )}

      {/* @mention dropdown */}
      {mentionOpen && (
        <div className="shrink-0 px-3 py-1 bg-white border-t border-[#e6e8e7] max-h-[150px] overflow-y-auto">
          {filteredMentionUsers.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertMention(u)}
              onMouseEnter={() => setActiveMention(i)}
              className={`w-full text-left px-2 py-1.5 flex items-center gap-2 rounded-lg transition-colors ${
                i === activeMention ? "bg-[#eaf5f1]" : "hover:bg-[#f7f9f8]"
              }`}
            >
              <div className="size-6 rounded-full bg-[#eaf5f1] flex items-center justify-center">
                <span className="text-[9px] font-semibold text-[#218760]">
                  {initialsOf(u.name)}
                </span>
              </div>
              <span className="text-[12px] font-medium text-[#374741]">
                {u.name}
              </span>
              <span className="text-[9px] text-[#9ba3a0] ml-auto">
                {u.role}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="shrink-0 px-3 py-2 bg-white border-t border-[#e6e8e7]">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.mp4,.doc,.docx,.webm"
          onChange={(e) => {
            if (e.target.files?.[0]) handleFile(e.target.files[0]);
            e.target.value = "";
          }}
        />
        <div className="flex items-end gap-2">
          {isRecording ? (
            <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-full">
              <div className="size-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[12px] font-semibold text-red-600 tabular-nums w-10">
                {fmtT(recordingTime)}
              </span>
              <div className="flex-1 flex items-center gap-0.5">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-1 rounded-full bg-red-300"
                    style={{
                      height: `${4 + Math.random() * 10}px`,
                      animation: "pulse 0.8s ease-in-out infinite",
                      animationDelay: `${i * 0.06}s`,
                    }}
                  />
                ))}
              </div>
              <button
                onClick={cancelRecording}
                className="p-1 rounded-full hover:bg-red-100 transition-colors"
                title="Cancel"
              >
                <X size={14} className="text-red-400" />
              </button>
              <button
                onClick={stopRecording}
                className="p-1 rounded-full bg-red-500 hover:bg-red-600 transition-colors"
                title="Send"
              >
                <Send size={12} className="text-white" />
              </button>
            </div>
          ) : (
            <div className="flex-1 flex items-end gap-2 px-3 py-1.5 bg-[#f7f9f8] rounded-[18px] border border-[#e6e8e7]">
              <textarea
                ref={inputRef}
                rows={1}
                // Cursor lands ready to type the moment a chat opens —
                // whether from the thread list or a toast click.
                autoFocus
                placeholder={
                  editing
                    ? "Edit your message…"
                    : "Message... (@ to mention · Shift+Enter = new line)"
                }
                value={text}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                onSelect={handleSelect}
                onClick={handleSelect}
                className="flex-1 min-w-0 bg-transparent border-none outline-none resize-none text-[12px] leading-[1.4] text-[#374741] placeholder:text-[#9ba3a0] py-[3px] whitespace-pre-wrap"
                style={{ scrollbarWidth: "thin" }}
              />
              {!editing && (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-0.5 mb-[3px] hover:bg-[#e6e8e7] rounded transition-colors"
                    disabled={isBusy}
                  >
                    <Paperclip size={14} className="text-[#697571]" />
                  </button>
                  <button
                    onClick={startRecording}
                    className="p-0.5 mb-[3px] hover:bg-[#e6e8e7] rounded transition-colors"
                    disabled={isBusy}
                  >
                    <Mic size={14} className="text-[#697571]" />
                  </button>
                </>
              )}
            </div>
          )}
          {!isRecording && (
            <button
              onClick={handleSend}
              disabled={isBusy}
              title={editing ? "Save (Enter)" : "Send (Enter)"}
              className="size-9 rounded-full bg-[#218760] hover:bg-[#1a6e4e] flex items-center justify-center transition-colors shrink-0"
            >
              {isBusy ? (
                <Loader2 size={14} className="text-white animate-spin" />
              ) : editing ? (
                <Check size={14} className="text-white" />
              ) : (
                <Send size={14} className="text-white" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Delete-for-everyone confirm (WhatsApp semantics) */}
      <ConfirmDialog
        open={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete for everyone?"
        tone="warning"
        warningTitle="This message will be deleted for everyone"
        warningDescription={`Participants will see "This message was deleted" instead.`}
        confirmLabel="Delete for everyone"
        loadingLabel="Deleting..."
        isLoading={deleteMessage.isPending}
      />
    </div>
  );
}

/* ─── Chat Bubble ─────────────────────────────────────── */

/** Real date + time on each message, e.g. "Today, 12:42 PM" / "Yesterday,
 *  5:10 PM" / "28 Jun, 2:30 PM" / "1 Jul 2025, 9:00 AM" (12-hour AM/PM;
 *  year only when not the current year). Previously time-only, so you couldn't
 *  tell which day a message was from. Mirrors the lead-panel chat's
 *  formatChatTimestamp. */
function formatChatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const time = d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;
  const datePart = d.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
  return `${datePart}, ${time}`;
}

function MiniChatBubble({
  msg,
  isMine,
  onReply,
  onDelete,
  onEdit,
  onJumpTo,
}: {
  msg: ChatMessage;
  isMine: boolean;
  onReply: () => void;
  onDelete: () => void;
  /** Edit — own live text messages only. */
  onEdit?: () => void;
  /** Reply-preview click → scroll to the quoted message. */
  onJumpTo?: (messageId: string) => void;
}) {
  const time = formatChatTimestamp(msg.createdAt);
  const isDeleted = msg.type === "deleted";
  const isAudio = msg.type === "audio" || msg.mediaMime?.startsWith("audio");
  const isImage = msg.type === "image" || msg.mediaMime?.startsWith("image");
  const isDoc = msg.mediaUrl && !isAudio && !isImage;

  return (
    <div
      data-message-id={msg.id}
      className={`group flex mb-1.5 items-end gap-1 rounded-xl transition-colors ${isMine ? "justify-end" : "justify-start"}`}
    >
      {/* Hover actions — left for mine (delete + reply), right for theirs */}
      {isMine && !isDeleted && (
        <>
          {onEdit && (
            <button
              onClick={onEdit}
              className="opacity-0 group-hover:opacity-100 p-0.5 text-[#9ba3a0] hover:text-[#374741] transition-all"
              title="Edit"
            >
              <Pencil size={12} />
            </button>
          )}
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 p-0.5 text-[#9ba3a0] hover:text-[#da2a46] transition-all"
            title="Delete for everyone"
          >
            <Trash2 size={12} />
          </button>
          <button
            onClick={onReply}
            className="opacity-0 group-hover:opacity-100 p-0.5 text-[#9ba3a0] hover:text-[#374741] transition-all"
            title="Reply"
          >
            <ArrowLeft size={12} className="rotate-180" />
          </button>
        </>
      )}
      <div
        className={`max-w-[300px] px-2.5 py-1.5 rounded-xl text-[12px] ${
          isMine
            ? "bg-[#d9fdd3] rounded-tr-sm"
            : "bg-white border border-[#e6e8e7] rounded-tl-sm"
        }`}
      >
        {/* Reply-to preview — click to jump to the quoted message */}
        {msg.replyTo && (
          <div
            role={onJumpTo ? "button" : undefined}
            tabIndex={onJumpTo ? 0 : undefined}
            title={onJumpTo ? "Go to the original message" : undefined}
            onClick={onJumpTo ? () => onJumpTo(msg.replyTo!.id) : undefined}
            onKeyDown={
              onJumpTo
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onJumpTo(msg.replyTo!.id);
                    }
                  }
                : undefined
            }
            className={`flex items-start gap-1.5 mb-1 px-2 py-1 bg-black/5 rounded-lg border-l-2 border-[#218760] ${
              onJumpTo ? "cursor-pointer hover:bg-black/10" : ""
            }`}
          >
            <div className="min-w-0">
              <p className="text-[9px] font-semibold text-[#218760] truncate">
                {bylineText(msg.replyTo.sender?.name, msg.replyTo.switchedByUser)}
              </p>
              <p className="text-[9px] text-[#697571] truncate">
                {msg.replyTo.type === "deleted"
                  ? "This message was deleted"
                  : msg.replyTo.body || msg.replyTo.type}
              </p>
            </div>
          </div>
        )}
        {/* "Switch user": a message a manager typed while viewing as this
            account always names them — including to the account's owner, who
            would otherwise read it as their own. `isMine` (senderId-based) is
            untouched and still drives alignment, style and read state. */}
        {(msg.switchedByUser?.name || (!isMine && msg.sender?.name)) && (
          <p
            className="text-[10px] font-semibold text-[#218760] mb-0.5 truncate"
            title={
              msg.switchedByUser?.name
                ? `${msg.switchedByUser.name} wrote this while viewing as ${msg.sender?.name}`
                : undefined
            }
          >
            {bylineText(msg.sender?.name, msg.switchedByUser)}
          </p>
        )}
        {/* Delete-for-everyone tombstone — body/media are cleared server-side */}
        {isDeleted && (
          <p className="flex items-center gap-1 italic text-[#9ba3a0]">
            <MessageCircleOff size={11} className="shrink-0" />
            {isMine ? "You deleted this message" : "This message was deleted"}
          </p>
        )}
        {/* Suppress the placeholder "Voice note" label — the player says it */}
        {msg.body && !(isAudio && msg.body === "Voice note") && (
          <p className={`text-[#1e302a] leading-[1.45] ${CHAT_BODY_CLASS}`}>
            {renderChatBody(msg.body, msg.mentions)}
          </p>
        )}
        {isAudio && msg.mediaUrl && (
          <ChatVoiceNote
            src={msg.mediaUrl}
            type={msg.mediaMime || undefined}
            isSent={isMine}
          />
        )}
        {isImage && msg.mediaUrl && (
          <img
            src={msg.mediaUrl}
            alt=""
            className="rounded-lg max-w-[220px] max-h-[150px] object-cover cursor-pointer mt-1"
            onClick={() => window.open(msg.mediaUrl!, "_blank")}
          />
        )}
        {isDoc && (
          <a
            href={msg.mediaUrl!}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 mt-1 px-2 py-1 bg-[#f0f2f1] rounded-lg hover:bg-[#e6e8e7] transition-colors"
          >
            <Paperclip size={10} className="text-[#697571] shrink-0" />
            <span className="text-[10px] text-[#374741] truncate">
              {msg.mediaName || "File"}
            </span>
          </a>
        )}
        <p
          className={`text-[9px] mt-0.5 ${isMine ? "text-[#697571] text-right" : "text-[#9ba3a0]"}`}
        >
          {msg.editedAt && <span className="italic mr-1">edited</span>}
          {time}
        </p>
      </div>
      {!isMine && !isDeleted && (
        <button
          onClick={onReply}
          className="opacity-0 group-hover:opacity-100 p-0.5 text-[#9ba3a0] hover:text-[#374741] transition-all"
          title="Reply"
        >
          <ArrowLeft size={12} className="rotate-180" />
        </button>
      )}
    </div>
  );
}

/* ─── New Chat (Lead Search) ──────────────────────────── */

function NewChatView({
  search,
  onSearchChange,
  onSelectLead,
  onBack,
}: {
  search: string;
  onSearchChange: (s: string) => void;
  onSelectLead: (lead: { id: number; name: string; phone: string }) => void;
  onBack: () => void;
}) {
  const { leads, isSearching } = useChatLeadSearch(search);

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 flex items-center gap-2 px-3 py-3 border-b border-[#e6e8e7] bg-white">
        <button
          onClick={onBack}
          className="p-1 rounded-lg hover:bg-[#f7f9f8] transition-colors"
        >
          <ArrowLeft className="size-4 text-[#374741]" />
        </button>
        <p className="text-[14px] font-semibold text-[#1e302a]">New Chat</p>
      </div>
      <div className="shrink-0 px-3 py-2 border-b border-[#e6e8e7] bg-white">
        <div className="flex items-center gap-2 px-2.5 py-2 bg-[#f7f9f8] rounded-lg border border-[#e6e8e7]">
          <Search className="size-3.5 text-[#9ba3a0] shrink-0" />
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            autoFocus
            className="flex-1 bg-transparent border-none outline-none text-[12px] text-[#374741] placeholder:text-[#9ba3a0]"
          />
          {search && (
            <button onClick={() => onSearchChange("")} className="p-0.5">
              <X size={12} className="text-[#9ba3a0]" />
            </button>
          )}
        </div>
      </div>
      <div
        className="flex-1 min-h-0 overflow-y-auto bg-white"
        style={{ scrollbarWidth: "thin" }}
      >
        {isSearching ? (
          <div className="flex items-center justify-center py-12">
            <span className="text-[11px] text-[#9ba3a0] animate-pulse">
              Loading...
            </span>
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-1">
            <p className="text-[12px] text-[#697571]">No leads found</p>
          </div>
        ) : (
          leads.map((lead) => (
            <button
              key={lead.id}
              onClick={() => onSelectLead(lead)}
              className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#f7f9f8] transition-colors border-b border-[#f0f2f1]"
            >
              <div className="size-9 rounded-full bg-[#eaf5f1] flex items-center justify-center shrink-0">
                <span className="text-[11px] font-semibold text-[#218760]">
                  {lead.name
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-[#1e302a] truncate">
                  {lead.name}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-[#9ba3a0]">
                    {lead.phone}
                  </span>
                  {lead.stage && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#f0f2f1] text-[#697571]">
                      {lead.stage}
                    </span>
                  )}
                </div>
              </div>
              <MessageSquare className="size-4 text-[#218760] shrink-0" />
            </button>
          ))
        )}
      </div>
    </div>
  );
}
